import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowBack, Check, Close, OpenInNew } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  integrationApiService,
  IntegrationConfig,
  IntegrationPlatform,
  IntegrationSettings,
} from '../services/integrationApi';
import { useOrgFeatureFlags } from '../hooks/useOrgFeatureFlags';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useToast } from '../contexts/ToastContext';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { useUnsavedChangesSource } from './appUpdate';
import ShopifyCustomAppWizard, {
  CUSTOM_APP_DRAFTS_QUERY_KEY,
  CustomAppWizardSnapshot,
} from './shopifyCustomAppWizard/ShopifyCustomAppWizard';
import { CUSTOM_APP_STEP_COUNT, CustomAppValues, resolveStoredDone } from './shopifyCustomAppWizard/customAppWizardModel';
import { tokens } from '../theme/tokens';
import { NumberField } from './ui';
import {
  SHOPIFY_PUBLIC_APP_INSTALL_ENABLED,
  resolveShopifyConnectMethod,
  shopifyConnectRecommendedMethod,
} from '../config/shopifyPublicAppInstall';
import { IntegrationLimitNotice } from './integrations/IntegrationLimitNotice';
import {
  isAtIntegrationLimit,
  type IntegrationCapacity,
} from './integrations/integrationLimitModel';

type ShopifyScopeGroup = 'inventory' | 'orders' | 'discounts' | 'markets';
type ShopifyMethod = 'app' | 'custom';
type CredMode = 'client' | 'token';
type LightspeedMethod = 'oauth' | 'manual';
type Phase = 'form' | 'verifying' | 'done';
type Screen = 'picker' | 'shopify' | 'lightspeed';

export type InstallerLaunch =
  | { kind: 'picker' }
  | {
      kind: 'shopify';
      shop?: string;
      method?: ShopifyMethod;
      /** When set, Admin API token path updates this integration instead of creating a second one. */
      reconnectIntegrationId?: string;
      error?: string;
      /** Open straight into the custom app setup wizard, for a store continuing a saved draft. */
      openCustomAppWizard?: boolean;
    };

interface PlatformOption {
  value: IntegrationPlatform;
  label: string;
  logo: string;
}

interface Props {
  launch: InstallerLaunch | null;
  onClose: () => void;
  platforms: PlatformOption[];
  onPickCredentialPlatform: (platform: IntegrationPlatform) => void;
  onScanCatalog: (integration: IntegrationConfig) => void;
  /** From GET /integrations/capacity. Missing means the page has not loaded it yet, so the form stays and the server still refuses. */
  capacity?: IntegrationCapacity | null;
  /** Platform admin viewing this store. */
  viewingAsAdmin?: boolean;
}

const METHOD_LINE: Record<IntegrationPlatform, string> = {
  shopify: 'Approve the Motzu app',
  bigcommerce: 'API credentials',
  woocommerce: 'Store URL and API keys',
  lightspeed: 'Sign in to Lightspeed',
  lightspeed_ecom: 'API key and secret',
  magento: 'API access token',
  prestashop: 'Webservice API key',
  opencart: 'API username and key',
};

const SHOPIFY_PERMS: { key: ShopifyScopeGroup; label: string; short: string; desc: string }[] = [
  { key: 'inventory', label: 'Inventory', short: 'Inventory', desc: 'Read and set stock when inventory sync is on. Turn off if your ERP owns quantity. Inventory sync is a plan feature; if your plan lacks it, it stays off and setup continues.' },
  { key: 'orders', label: 'Orders', short: 'Orders', desc: 'Read order line items for sell-through and rebate reports. Never changes your storefront.' },
  { key: 'discounts', label: 'Checkout discounts', short: 'Discounts', desc: 'Apply vendor rebates as Shopify discounts. Turn off if you manage discounts elsewhere.' },
  { key: 'markets', label: 'Markets and price zones', short: 'Markets', desc: 'Read markets and set a per-zone price. Turn off if you don’t use Shopify Markets pricing.' },
];

export const LIGHTSPEED_INSTALL_PREFS_KEY = 'motzu.lightspeedInstallPrefs';

const SHOP_PATTERN = /^[a-z0-9][a-z0-9-]{1,}$/;
const ACCOUNT_PATTERN = /^\d{3,}$/;

function normalizeShop(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '')
    .replace(/\.myshopify\.com$/, '');
}

function defaultSettings(
  syncInventory: boolean,
  initialSync?: { startingCount: number | undefined; unmatchedProductAction?: 'create' | 'skip' },
): IntegrationSettings {
  return {
    syncProducts: true,
    syncInventory,
    syncPricing: true,
    syncImages: true,
    addMotzuTag: true,
    autoSync: false,
    syncInterval: 'manual',
    syncMode: 'override',
    // Flag `initial_sync_mode` (ignored by the server while it is off): a new Shopify integration
    // creates the full listing of a selected product on its first sync. Existing integrations keep
    // the missing-means-skip default.
    ...(initialSync
      ? {
          unmatchedProductAction: initialSync.unmatchedProductAction ?? ('create' as const),
          ...(initialSync.startingCount != null ? { defaultInventoryCount: initialSync.startingCount } : {}),
        }
      : {}),
    groupKitBodyAsVariants: true,
    fieldMappings: [],
  };
}

const IntegrationInstallWizard: React.FC<Props> = ({
  launch,
  onClose,
  platforms,
  onPickCredentialPlatform,
  onScanCatalog,
  capacity,
  viewingAsAdmin,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const { isEnabled } = useOrgFeatureFlags();
  const lightspeedSalesEnabled = isEnabled('lightspeed_sales_sync');
  const lightspeedLinkOnlyEnabled = isEnabled('lightspeed_link_only');
  const initialSyncEnabled = isEnabled('initial_sync_mode');
  const shopifyScanFirst = isEnabled('shopify_scan_before_selection');
  const [startingCount, setStartingCount] = useState<number | undefined>(1);
  const [unmatchedAction, setUnmatchedAction] = useState<'create' | 'skip'>('create');

  const [screen, setScreen] = useState<Screen>(launch?.kind === 'shopify' ? 'shopify' : 'picker');
  const [step, setStep] = useState(0);
  const [phase, setPhase] = useState<Phase>('form');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<IntegrationConfig | null>(null);

  const [shop, setShop] = useState('');
  const [method, setMethod] = useState<ShopifyMethod>(() => resolveShopifyConnectMethod());
  const [credMode, setCredMode] = useState<CredMode>('client');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [installLink, setInstallLink] = useState('');
  const [installUrls, setInstallUrls] = useState<{ appUrl: string; redirectUrl: string } | null>(null);
  const [token, setToken] = useState('');
  const [perms, setPerms] = useState<Record<ShopifyScopeGroup, boolean>>({
    inventory: true,
    orders: true,
    discounts: true,
    markets: true,
  });

  const [lsMethod, setLsMethod] = useState<LightspeedMethod>('oauth');
  const [lsAccountId, setLsAccountId] = useState('');
  const [lsAccessToken, setLsAccessToken] = useState('');
  const [lsRefreshToken, setLsRefreshToken] = useState('');
  const [lsClientId, setLsClientId] = useState('');
  const [lsClientSecret, setLsClientSecret] = useState('');
  const [lsPerms, setLsPerms] = useState({ sales: true });
  // Reconnect target from the launch. Cleared when the user leaves the Shopify
  // screen, and the shop field is locked while set, so a reconnect can never be
  // pointed at a different store.
  const [reconnectId, setReconnectId] = useState<string | undefined>(
    launch?.kind === 'shopify' ? launch.reconnectIntegrationId : undefined,
  );
  // Apply a new launch before paint so a reconnect does not flash the limit notice.
  const [appliedLaunch, setAppliedLaunch] = useState(launch);
  if (appliedLaunch !== launch) {
    setAppliedLaunch(launch);
    setScreen(launch?.kind === 'shopify' ? 'shopify' : 'picker');
    setReconnectId(launch?.kind === 'shopify' ? launch.reconnectIntegrationId : undefined);
  }
  // Which saved app the store chose to inspect. A new launch starts collapsed so Continue is the next action.
  const [setupOpenKey, setSetupOpenKey] = useState<string | null>(null);
  // The custom app setup wizard takes over this dialog while it is open.
  const [wizardOpen, setWizardOpen] = useState(false);
  // Its place and values for this session, secret included, so leaving it to look at the options loses nothing.
  const [wizardSnapshot, setWizardSnapshot] = useState<CustomAppWizardSnapshot | null>(null);

  useEffect(() => {
    if (!launch) return;
    setSetupOpenKey(null);
    setWizardOpen(launch.kind === 'shopify' && Boolean(launch.openCustomAppWizard));
    setWizardSnapshot(null);
    setScreen(launch.kind === 'picker' ? 'picker' : 'shopify');
    setStep(0);
    setPhase('form');
    setBusy(false);
    setCreated(null);
    setError(launch.kind === 'shopify' ? launch.error || null : null);
    setShop(launch.kind === 'shopify' ? launch.shop || '' : '');
    setMethod(
      resolveShopifyConnectMethod(launch.kind === 'shopify' ? launch.method : undefined),
    );
    setCredMode('client');
    setClientId('');
    setClientSecret('');
    setInstallLink('');
    setToken('');
    setPerms({ inventory: true, orders: true, discounts: true, markets: true });
    setLsMethod('oauth');
    setLsAccountId('');
    setLsAccessToken('');
    setLsRefreshToken('');
    setLsClientId('');
    setLsClientSecret('');
    setLsPerms({ sales: true });
    setReconnectId(launch.kind === 'shopify' ? launch.reconnectIntegrationId : undefined);
  }, [launch]);

  useEffect(() => {
    if (screen !== 'shopify') return;
    let cancelled = false;
    integrationApiService.getShopifyInstallUrls()
      .then((urls) => {
        if (!cancelled) setInstallUrls(urls);
      })
      .catch(() => {
        if (!cancelled) setInstallUrls(null);
      });
    return () => {
      cancelled = true;
    };
  }, [screen]);

  const isLs = screen === 'lightspeed';
  const wizardVisible = wizardOpen && screen === 'shopify' && launch !== null;
  const isCustom = method === 'custom';
  const shopValid = SHOP_PATTERN.test(shop);
  const shopError = shop.length > 0 && !shopValid;
  const accountError = lsAccountId.length > 0 && !ACCOUNT_PATTERN.test(lsAccountId);
  const lsManual = lsMethod === 'manual';

  const { data: savedCustomApps, isLoading: savedAppsLoading } = useQuery({
    queryKey: ['shopify-custom-apps'],
    queryFn: () => integrationApiService.getShopifyCustomApps(),
    enabled: screen === 'shopify' && isCustom,
    staleTime: 30_000,
  });
  const savedApp = shopValid ? savedCustomApps?.find((app) => app.shopId === shop) : undefined;
  const { data: customAppDrafts } = useQuery({
    queryKey: CUSTOM_APP_DRAFTS_QUERY_KEY,
    queryFn: () => integrationApiService.getShopifyCustomAppDrafts(),
    enabled: screen === 'shopify' && isCustom && !wizardOpen,
    staleTime: 30_000,
    retry: false,
  });
  const customAppDraft = shopValid ? customAppDrafts?.find((draft) => draft.shopId === shop) : undefined;
  const enteredClientCreds = Boolean(clientId.trim() && clientSecret.trim());
  // Blank fields with an app saved for this shop continue its install; typing replaces it.
  const useSavedApp = isCustom && credMode === 'client' && !!savedApp && !clientId.trim() && !clientSecret.trim();
  const savedKey = savedApp ? `${shop}:${savedApp.clientId}` : '';
  const credentialsSetupOpen = savedKey !== '' && setupOpenKey === savedKey;
  // Saved credentials: hide the connection choice, the Dev Dashboard steps and the empty fields until asked.
  const resumeCollapsed = useSavedApp && !credentialsSetupOpen;
  // Don't paint the long setup while the saved-app lookup is still in flight, or it flashes open and then collapses.
  const holdShopifySetup = isCustom && credMode === 'client' && shopValid && savedAppsLoading && !savedCustomApps;
  const showShopifySetup = !resumeCollapsed && !holdShopifySetup;
  const [savingForLater, setSavingForLater] = useState(false);

  const saveForLater = async (values: CustomAppValues): Promise<boolean> => {
    if (!shopValid) return false;
    setSavingForLater(true);
    setError(null);
    try {
      await integrationApiService.saveShopifyCustomApp(shop, values.clientId.trim(), values.clientSecret.trim(), values.installLink);
      queryClient.invalidateQueries({ queryKey: ['shopify-custom-apps'] });
      queryClient.invalidateQueries({ queryKey: CUSTOM_APP_DRAFTS_QUERY_KEY });
      showToast(t('integrations.customAppResume.savedForLater'), 'success');
      onClose();
      return true;
    } catch (err) {
      setError((err as Error)?.message || t('integrations.customAppResume.saveFailed'));
      return false;
    } finally {
      setSavingForLater(false);
    }
  };

  // Until it is saved, the secret lives only in this dialog, whether or not the wizard is showing.
  useUnsavedChangesSource({
    dirty: launch !== null && Boolean(clientSecret || wizardSnapshot?.clientSecret),
    discard: () => {
      setClientSecret('');
      setWizardSnapshot((snapshot) => (snapshot ? { ...snapshot, clientSecret: '' } : snapshot));
    },
  });

  const existingCustomApp = launch?.kind === 'shopify' && launch.method === 'custom';
  const recommendedMethod = shopifyConnectRecommendedMethod(existingCustomApp);
  const motzuAppInstallEnabled = SHOPIFY_PUBLIC_APP_INSTALL_ENABLED;
  // A new connection with no app yet: Continue opens the setup wizard, which fills in the credentials.
  const needsCustomAppWizard =
    !isLs && isCustom && credMode === 'client' && !reconnectId && !useSavedApp && !enteredClientCreds;
  const shopifyStepValid =
    shopValid &&
    (method === 'app'
      ? motzuAppInstallEnabled
      : (credMode === 'client' && !holdShopifySetup && (needsCustomAppWizard || enteredClientCreds || useSavedApp)) ||
        (credMode === 'token' && token.trim()));
  const lightspeedStepValid = !lsManual || (ACCOUNT_PATTERN.test(lsAccountId) && lsAccessToken.trim().length > 0);
  const canNext = isLs ? (step === 0 ? lightspeedStepValid : true) : (step === 0 ? Boolean(shopifyStepValid) : true);

  const logoFor = (platform: IntegrationPlatform) =>
    platforms.find((option) => option.value === platform)?.logo;

  const openPlatform = (platform: IntegrationPlatform) => {
    if (isAtIntegrationLimit(capacity)) return;
    if (platform === 'shopify' || platform === 'lightspeed') {
      setReconnectId(undefined);
      setScreen(platform);
      if (platform === 'shopify') {
        setMethod(resolveShopifyConnectMethod());
      }
      setStep(0);
      setPhase('form');
      setError(null);
      return;
    }
    onPickCredentialPlatform(platform);
  };

  const scopeGroups = SHOPIFY_PERMS.filter((perm) => perms[perm.key]).map((perm) => perm.key);

  // A token connect ends any setup started for this shop. Only once the token is proven good, so a
  // mistyped one does not lose the store's place. Best effort: the connection already succeeded.
  const clearCustomAppDraft = () => {
    integrationApiService
      .deleteShopifyCustomAppDraft(shop)
      .then(() => queryClient.invalidateQueries({ queryKey: CUSTOM_APP_DRAFTS_QUERY_KEY }))
      .catch(() => undefined);
  };

  const finishLocal = async () => {
    if (isAtIntegrationLimit(capacity) && !reconnectId) return;
    setBusy(true);
    setError(null);
    setPhase('verifying');
    try {
      if (!isLs && reconnectId) {
        // The server tests the token before saving it, so a wrong token leaves
        // the working credentials in place.
        const integration = await integrationApiService.reconnectShopifyToken(reconnectId, shop, token.trim());
        clearCustomAppDraft();
        queryClient.invalidateQueries({ queryKey: ['integrations'] });
        setCreated(integration);
        setPhase('done');
        return;
      }
      const integration = isLs
        ? await integrationApiService.createIntegration({
            platform: 'lightspeed',
            name: `Lightspeed (${lsAccountId})`,
            isActive: true,
            credentials: {
              accountId: lsAccountId,
              accessToken: lsAccessToken,
              ...(lsRefreshToken.trim() ? { refreshToken: lsRefreshToken.trim() } : {}),
              ...(lsClientId.trim() ? { clientId: lsClientId.trim() } : {}),
              ...(lsClientSecret.trim() ? { clientSecret: lsClientSecret.trim() } : {}),
            },
            // Lightspeed is the POS source of truth for inventory; Motzu never pushes counts into it.
            settings: defaultSettings(false),
          })
        : await integrationApiService.createIntegration({
            platform: 'shopify',
            name: `Shopify (${shop})`,
            isActive: true,
            credentials: {
              shopId: shop,
              shopDomain: `${shop}.myshopify.com`,
              accessToken: token.trim(),
              apiVersion: '2026-04',
              customApp: true,
            },
            settings: defaultSettings(perms.inventory, initialSyncEnabled ? { startingCount, unmatchedProductAction: unmatchedAction } : undefined),
          });
      try {
        const result = await integrationApiService.testIntegration(integration.id);
        if (result?.success !== true) {
          throw new Error(result?.message || 'Could not connect with those credentials.');
        }
      } catch (testError) {
        await integrationApiService.deleteIntegration(integration.id).catch(() => undefined);
        throw testError;
      }
      const enableOrders = isLs ? lightspeedSalesEnabled && lsPerms.sales : perms.orders;
      if (enableOrders) {
        const headers = await getAuthHeaders();
        await makeApiCall(`/integrations/${integration.id}/order-sync`, {
          method: 'PATCH',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderSyncEnabled: true }),
        }).catch(() => undefined);
      }
      if (!isLs) clearCustomAppDraft();
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      queryClient.invalidateQueries({ queryKey: ['integration-capacity'] });
      setCreated(integration);
      setPhase('done');
    } catch (err) {
      setPhase('form');
      setError((err as Error)?.message || 'Could not connect. Check the credentials and try again.');
    } finally {
      setBusy(false);
    }
  };

  const startOAuth = async () => {
    if (isAtIntegrationLimit(capacity) && !reconnectId) return;
    setBusy(true);
    setError(null);
    try {
      if (isLs) {
        const data = await integrationApiService.startLightspeedInstall(
          reconnectId ? { reconnectIntegrationId: reconnectId } : undefined,
        );
        sessionStorage.setItem(
          LIGHTSPEED_INSTALL_PREFS_KEY,
          JSON.stringify({ sales: lsPerms.sales }),
        );
        window.location.assign(data.installUrl);
        return;
      }
      if (method === 'app' && !motzuAppInstallEnabled) {
        setBusy(false);
        setMethod('custom');
        return;
      }
      const data = await integrationApiService.startShopifyInstall(shop, {
        ...(isCustom && credMode === 'client'
          ? {
              ...(useSavedApp
                ? { useSavedCustomApp: true }
                : { customClientId: clientId.trim(), customClientSecret: clientSecret.trim() }),
              installLink,
            }
          : {}),
        scopeGroups,
        ...(initialSyncEnabled ? { unmatchedProductAction: unmatchedAction } : {}),
      });
      if (data.customApp && data.needsInstallLink) {
        // Shopify installs a custom-distribution app the first time only from its own install
        // link; its launch brings the store back to Motzu, which finishes with the saved app.
        queryClient.invalidateQueries({ queryKey: ['shopify-custom-apps'] });
        if (!data.customAppInstallLink) {
          setBusy(false);
          setStep(0);
          setError(t('integrations.customAppResume.installLinkRequired'));
          return;
        }
        window.location.assign(data.customAppInstallLink);
        return;
      }
      window.location.assign(data.installUrl);
    } catch (err) {
      setBusy(false);
      setError((err as Error)?.message || 'Failed to start install');
    }
  };

  const onPrimary = () => {
    if (phase === 'done') {
      // Never the product browser: the walkthrough (when on) or the integration's own scan.
      if (created) onScanCatalog(created);
      else onClose();
      return;
    }
    if (!canNext || busy) return;
    if (step === 0 && needsCustomAppWizard) {
      setError(null);
      setWizardOpen(true);
      return;
    }
    if (step < 2) {
      setStep(step + 1);
      return;
    }
    const localConnect = isLs ? lsManual : isCustom && credMode === 'token';
    if (localConnect) {
      void finishLocal();
      return;
    }
    void startOAuth();
  };

  const stepLabels = isLs ? ['Account', 'Data', 'Review'] : ['Store', 'Permissions', 'Review'];
  const platformName = isLs ? 'Lightspeed' : 'Shopify';
  const shopDomain = shop ? `${shop}.myshopify.com` : '';
  const targetName = isLs
    ? lsManual && lsAccountId
      ? `Account ${lsAccountId}`
      : 'Lightspeed'
    : shopDomain || 'Shopify';

  const accessSummary = isLs
    ? ['Catalog', ...(lightspeedSalesEnabled && lsPerms.sales ? ['Sales'] : [])].join(', ')
    : ['Catalog', ...SHOPIFY_PERMS.filter((perm) => perms[perm.key]).map((perm) => perm.short)].join(', ');

  const methodLabel = isLs
    ? lsManual
      ? 'Manual credentials'
      : 'Lightspeed sign-in'
    : isCustom
      ? credMode === 'client'
        ? 'Custom app · client credentials'
        : 'Custom app · Admin API token'
      : 'Motzu app';

  const copiesText = isLs
    ? `Item SKU, UPC, manufacturer SKU, description, quantity per shop, and price.${
        lightspeedSalesEnabled && lsPerms.sales
          ? ' From sales: sale date and number, line quantities, prices, and discounts.'
          : ''
      }`
    : `SKU, barcode, product name, quantity, price, and currency.${
        perms.orders
          ? ' From orders: discounts, order date and number, and payment or fulfillment status.'
          : ''
      }`;

  const laterNote = isLs
    ? lsManual
      ? 'Your token must belong to an employee with full access. You can change these switches later in Integrations → Sync Behavior.'
      : 'You can change these later in Integrations → Sync Behavior.'
    : isCustom
      ? 'Your custom app must also allow these in its Admin API scopes. To add one later, enable the scope on the app, then choose Check permissions on the integration card.'
      : 'You can change these later in Integrations → Sync Behavior. Anything you turn off stays locked until you grant it in Shopify.';

  let primaryLabel = 'Continue';
  let primaryExternal = false;
  if (phase === 'done') {
    primaryLabel = 'Scan catalog';
  } else if (step === 2) {
    if (isLs) {
      primaryLabel = lsManual ? 'Connect account' : 'Continue to Lightspeed';
      primaryExternal = !lsManual;
    } else if (!isCustom) {
      primaryLabel = 'Continue to Shopify';
      primaryExternal = true;
    } else {
      primaryLabel = credMode === 'client' ? 'Install custom app' : 'Connect store';
      primaryExternal = credMode === 'client';
    }
  }

  const headerSub =
    phase === 'done' ? 'Connected' : phase === 'verifying' ? 'Verifying' : `Step ${step + 1} of 3 · ${stepLabels[step]}`;

  const leaveWizard = (snapshot: CustomAppWizardSnapshot) => {
    setWizardSnapshot(snapshot);
    setWizardOpen(false);
    setError(null);
  };

  const finishWizard = (snapshot: CustomAppWizardSnapshot) => {
    setClientId(snapshot.clientId.trim());
    setClientSecret(snapshot.clientSecret.trim());
    setInstallLink(snapshot.installLink.trim());
    leaveWizard(snapshot);
    setStep(1);
  };

  // A reconnect of an integration the store already has is not a new connection.
  // Only the form is blocked. finishLocal invalidates capacity, and a store that
  // just used its last slot would otherwise replace the Connected screen with
  // this notice (MOT-808).
  const blockedByLimit = isAtIntegrationLimit(capacity) && !reconnectId && phase === 'form';

  if (blockedByLimit) {
    return (
      <Dialog
        open={launch !== null}
        onClose={onClose}
        fullWidth
        fullScreen={fullScreen}
        PaperProps={{ sx: { width: '100%', maxWidth: 560, borderRadius: '16px', boxShadow: tokens.shadow.menu } }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: tokens.ink.primary }}>
                Add an integration
              </Typography>
            </Box>
            <IconButton aria-label="Close" onClick={onClose} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <IntegrationLimitNotice capacity={capacity} viewingAsAdmin={viewingAsAdmin} />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={launch !== null}
      onClose={busy ? undefined : onClose}
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: wizardVisible ? 980 : screen === 'picker' ? 780 : 620,
          borderRadius: '16px',
          boxShadow: tokens.shadow.menu,
        },
      }}
    >
      {wizardVisible ? (
        <ShopifyCustomAppWizard
          shop={shop}
          installUrls={installUrls}
          scopeGroups={scopeGroups}
          resume={wizardSnapshot}
          error={error}
          savingForLater={savingForLater}
          onBack={leaveWizard}
          onClose={onClose}
          onUseToken={(snapshot) => {
            leaveWizard(snapshot);
            setCredMode('token');
          }}
          onSaveForLater={saveForLater}
          onFinish={finishWizard}
        />
      ) : screen === 'picker' ? (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: tokens.ink.primary }}>
                Add an integration
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: 13.5, color: tokens.ink.muted }}>
                Pick the platform your store runs on. We’ll walk you through the rest.
              </Typography>
            </Box>
            <IconButton aria-label="Close" onClick={onClose} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fill, minmax(220px, 1fr))' },
              gap: 1.5,
            }}
          >
            {platforms.map((option) => (
              <Box
                key={option.value}
                role="button"
                tabIndex={0}
                onClick={() => openPlatform(option.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openPlatform(option.value);
                  }
                }}
                sx={{
                  p: 2,
                  borderRadius: `${tokens.radius.card}px`,
                  border: '1px solid',
                  borderColor: tokens.line.default,
                  cursor: 'pointer',
                  bgcolor: tokens.surface.card,
                  '&:hover': {
                    borderColor: tokens.accent.border,
                    boxShadow: `0 0 0 3px ${tokens.accent.tint}`,
                  },
                }}
              >
                <Box sx={{ height: 32, display: 'flex', alignItems: 'center', mb: 1.25 }}>
                  <Box
                    component="img"
                    src={option.logo}
                    alt=""
                    sx={{ maxHeight: 32, maxWidth: 150, objectFit: 'contain' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: tokens.ink.primary }}>
                    {option.label}
                  </Typography>
                  {option.value === 'shopify' && (
                    <Box
                      component="span"
                      sx={{
                        px: 0.75,
                        py: 0.25,
                        borderRadius: `${tokens.radius.chip}px`,
                        bgcolor: tokens.signal.positive.tint,
                        color: tokens.signal.positive.ink,
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      Popular
                    </Box>
                  )}
                </Box>
                <Typography sx={{ mt: 0.5, fontSize: 12.5, fontWeight: 500, color: tokens.ink.muted }}>
                  {METHOD_LINE[option.value]}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 3,
              py: 2.5,
              borderBottom: '1px solid',
              borderColor: tokens.line.hairline,
            }}
          >
            {phase === 'form' && step === 0 && (
              <IconButton
                aria-label="Back to platforms"
                size="small"
                onClick={() => {
                  setReconnectId(undefined);
                  setScreen('picker');
                  setError(null);
                }}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
            )}
            {logoFor(isLs ? 'lightspeed' : 'shopify') && (
              <Box sx={{ height: 28, display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
                <Box
                  component="img"
                  src={logoFor(isLs ? 'lightspeed' : 'shopify')}
                  alt=""
                  sx={{ maxHeight: 28, maxWidth: 120, objectFit: 'contain' }}
                />
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 16.5, fontWeight: 700, color: tokens.ink.primary, lineHeight: 1.2 }}>
                Connect {platformName}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{headerSub}</Typography>
            </Box>
            <IconButton aria-label="Close" onClick={onClose} size="small" disabled={busy}>
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {phase === 'form' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 1.75, bgcolor: tokens.surface.sunken }}>
              {stepLabels.map((label, index) => {
                const done = index < step;
                const current = index === step;
                return (
                  <React.Fragment key={label}>
                    {index > 0 && (
                      <Box sx={{ flex: 1, height: '1.5px', bgcolor: done || current ? tokens.accent.main : tokens.ink.disabled }} />
                    )}
                    <Box
                      component="button"
                      type="button"
                      disabled={!done}
                      onClick={() => done && setStep(index)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        border: 'none',
                        background: 'none',
                        p: 0,
                        cursor: done ? 'pointer' : 'default',
                        color: done || current ? tokens.accent.main : tokens.ink.faint,
                      }}
                    >
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          fontSize: 12,
                          fontWeight: 700,
                          color: done ? tokens.ink.inverse : current ? tokens.accent.main : tokens.ink.faint,
                          bgcolor: done ? tokens.accent.main : 'transparent',
                          border: '1.5px solid',
                          borderColor: done || current ? tokens.accent.main : tokens.ink.disabled,
                        }}
                      >
                        {done ? <Check sx={{ fontSize: 14 }} /> : index + 1}
                      </Box>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'inherit' }}>{label}</Typography>
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>
          )}

          {error && phase === 'form' && (
            <Alert severity="error" sx={{ mx: 3, mt: 2 }}>
              {error}
            </Alert>
          )}

          {phase === 'form' && step === 0 && !isLs && (
            <Box sx={{ px: 3, py: 2.75, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
              <Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.ink.body, mb: 0.75 }}>
                  Shopify store
                </Typography>
                <TextField
                  value={shop}
                  onChange={(event) => setShop(normalizeShop(event.target.value))}
                  placeholder="your-store"
                  fullWidth
                  disabled={Boolean(reconnectId)}
                  error={shopError}
                  InputProps={{
                    endAdornment: (
                      <Typography sx={{ fontSize: 13, color: tokens.ink.muted, whiteSpace: 'nowrap' }}>
                        .myshopify.com
                      </Typography>
                    ),
                    sx: {
                      fontFamily: tokens.font.mono,
                      fontSize: 13,
                      ...(shopValid ? { boxShadow: `0 0 0 3px ${tokens.accent.tint}` } : {}),
                    },
                  }}
                  helperText={
                    shopError
                      ? 'Use only letters, numbers, and hyphens, like b2c78f-3.'
                      : reconnectId
                        ? 'Reconnecting this store. To connect a different store, go back and add it as a new integration.'
                        : undefined
                  }
                />
                {!shopError && showShopifySetup && (
                  <Typography sx={{ mt: 0.75, fontSize: 12, color: tokens.ink.muted }}>
                    Find it in Shopify admin under Settings → Domains. Pasting the full URL works too.{' '}
                    <Box
                      component="a"
                      href="https://admin.shopify.com/settings/domains"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ fontWeight: 600, color: tokens.accent.main }}
                    >
                      Open Shopify domains
                    </Box>
                  </Typography>
                )}
              </Box>

              {savedApp && isCustom && credMode === 'client' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
                  <Alert severity={savedApp.status === 'failed' ? 'warning' : 'info'} sx={{ width: '100%' }}>
                    {credentialsSetupOpen && !reconnectId
                      ? t('integrations.customAppWizard.installer.savedReplace', { clientId: savedApp.clientId })
                      : t(
                          credentialsSetupOpen
                            ? 'integrations.customAppResume.wizardSaved'
                            : 'integrations.customAppResume.wizardSavedReady',
                          { clientId: savedApp.clientId },
                        )}
                    {savedApp.lastError && (
                      <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                        {t('integrations.customAppResume.wizardLastError', { message: savedApp.lastError.message })}
                      </Box>
                    )}
                    {useSavedApp && savedApp.needsInstallLink && (
                      <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                        {t(
                          savedApp.installLink
                            ? 'integrations.customAppResume.wizardOpensLink'
                            : 'integrations.customAppResume.wizardNeedsLink',
                        )}
                      </Box>
                    )}
                  </Alert>
                  {useSavedApp && savedApp.needsInstallLink && !savedApp.installLink && (
                    <TextField
                      label={t('integrations.customAppResume.linkField')}
                      value={installLink}
                      onChange={(event) => setInstallLink(event.target.value)}
                      placeholder="https://admin.shopify.com/…"
                      helperText={t('integrations.customAppResume.linkFieldHelp')}
                      fullWidth
                    />
                  )}
                  {useSavedApp && (
                    <Button
                      size="small"
                      variant="text"
                      aria-expanded={credentialsSetupOpen}
                      onClick={() => setSetupOpenKey(credentialsSetupOpen ? null : savedKey)}
                      sx={{ fontWeight: 600 }}
                    >
                      {t(credentialsSetupOpen ? 'integrations.customAppResume.hideSetup' : 'integrations.customAppResume.viewSetup')}
                    </Button>
                  )}
                </Box>
              )}

              {holdShopifySetup && (
                <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{t('integrations.customAppWizard.loading')}</Typography>
              )}

              {showShopifySetup && (
              <>
              <Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.ink.body, mb: 1 }}>
                  How Motzu connects
                </Typography>
                <Box
                  role="radiogroup"
                  aria-label="How Motzu connects"
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                >
                  {(
                    [
                      {
                        key: 'app' as const,
                        label: 'Motzu app',
                        desc: 'Approve Motzu in Shopify. We handle API access and tokens for you.',
                        disabled: !motzuAppInstallEnabled,
                      },
                      {
                        key: 'custom' as const,
                        label: 'Your own custom app',
                        desc: 'Use credentials from an app your team or agency created in Shopify admin.',
                        disabled: false,
                      },
                    ]
                  ).map((option) => {
                    const selected = method === option.key;
                    const recommended = recommendedMethod === option.key && !option.disabled;
                    return (
                      <Box
                        key={option.key}
                        role="radio"
                        aria-checked={selected}
                        aria-disabled={option.disabled || undefined}
                        tabIndex={option.disabled ? -1 : 0}
                        onClick={() => {
                          if (!option.disabled) setMethod(option.key);
                        }}
                        onKeyDown={(event) => {
                          if (option.disabled) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setMethod(option.key);
                          }
                        }}
                        sx={{
                          display: 'flex',
                          gap: 1.5,
                          p: 1.75,
                          borderRadius: `${tokens.radius.card}px`,
                          border: '1px solid',
                          borderColor: selected ? tokens.accent.main : tokens.line.default,
                          bgcolor: selected ? tokens.accent.tint : tokens.surface.card,
                          cursor: option.disabled ? 'not-allowed' : 'pointer',
                          opacity: option.disabled ? 0.65 : 1,
                        }}
                      >
                        <Box
                          sx={{
                            mt: 0.25,
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            border: '1.5px solid',
                            borderColor: selected ? tokens.accent.main : tokens.ink.disabled,
                            display: 'grid',
                            placeItems: 'center',
                            flex: '0 0 auto',
                          }}
                        >
                          {selected && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tokens.accent.main }} />}
                        </Box>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: option.disabled ? tokens.ink.faint : undefined }}>
                              {option.label}
                            </Typography>
                            {recommended && (
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: tokens.signal.positive.ink }}>
                                Recommended
                              </Typography>
                            )}
                            {option.disabled && (
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: tokens.ink.faint }}>
                                {t('integrations.shopifyConnectMethod.comingSoon')}
                              </Typography>
                            )}
                          </Box>
                          <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, mt: 0.25 }}>{option.desc}</Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {isCustom && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', gap: 0.5, p: 0.375, width: 'fit-content', border: '1px solid', borderColor: tokens.line.default, borderRadius: '9px' }}>
                    {(
                      [
                        ['client', 'Client credentials'],
                        ['token', 'Admin API token'],
                      ] as const
                    ).map(([key, label]) => (
                      <Button
                        key={key}
                        size="small"
                        onClick={() => setCredMode(key)}
                        sx={{
                          px: 1.5,
                          fontSize: 12.5,
                          fontWeight: 600,
                          color: credMode === key ? tokens.accent.hover : tokens.ink.muted,
                          bgcolor: credMode === key ? tokens.accent.tint : 'transparent',
                          borderRadius: `${tokens.radius.chip}px`,
                        }}
                      >
                        {label}
                      </Button>
                    ))}
                  </Box>
                  {credMode === 'client' ? (
                    reconnectId ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                          <TextField label="Client ID" value={clientId} onChange={(event) => setClientId(event.target.value)} fullWidth />
                          <TextField label="Client secret" type="password" value={clientSecret} onChange={(event) => setClientSecret(event.target.value)} fullWidth />
                        </Box>
                        <Box>
                          <Button size="small" variant="text" disabled={!shopValid} onClick={() => setWizardOpen(true)} sx={{ fontWeight: 600 }}>
                            {t('integrations.shopifyConnectMethod.reconnectNewApp')}
                          </Button>
                        </Box>
                      </Box>
                    ) : enteredClientCreds ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 1.5,
                          p: 1.75,
                          borderRadius: `${tokens.radius.card}px`,
                          border: '1px solid',
                          borderColor: tokens.signal.positive.border,
                          bgcolor: tokens.signal.positive.tint,
                        }}
                      >
                        <Check sx={{ flex: '0 0 auto', fontSize: 18, color: tokens.signal.positive.ink }} />
                        <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: tokens.ink.primary }}>
                            {t('integrations.customAppWizard.installer.ready')}
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, overflowWrap: 'anywhere' }}>
                            {t('integrations.customAppWizard.installer.readyLine', { clientId })}
                          </Typography>
                        </Box>
                        <Button size="small" variant="outlined" onClick={() => setWizardOpen(true)}>
                          {t('integrations.customAppWizard.installer.edit')}
                        </Button>
                      </Box>
                    ) : useSavedApp ? (
                      <Box>
                        <Button size="small" variant="text" disabled={!shopValid} onClick={() => setWizardOpen(true)} sx={{ fontWeight: 600 }}>
                          {t('integrations.customAppWizard.installer.newApp')}
                        </Button>
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: 12.5, color: tokens.ink.bodyAlt }}>
                        {customAppDraft
                          ? t('integrations.customAppWizard.installer.inProgress', {
                              done: resolveStoredDone(customAppDraft.done).length,
                              total: CUSTOM_APP_STEP_COUNT,
                            })
                          : t('integrations.customAppWizard.installer.start')}
                      </Typography>
                    )
                  ) : (
                    <TextField
                      label="Admin API access token"
                      type="password"
                      placeholder="shpat_…"
                      value={token}
                      onChange={(event) => setToken(event.target.value)}
                      fullWidth
                    />
                  )}
                  {(credMode === 'token' || reconnectId) && (
                    <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>
                      {credMode === 'client'
                        ? 'Client ID and secret are under the app’s API credentials in the Dev Dashboard. The app needs the scopes you choose in the next step.'
                        : 'A token already carries the scopes set on its app. To add more later, enable them on the app, then choose Check permissions.'}
                    </Typography>
                  )}
                </Box>
              )}
              </>
              )}
            </Box>
          )}

          {phase === 'form' && step === 0 && isLs && (
            <Box sx={{ px: 3, py: 2.75, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {(
                [
                  { key: 'oauth' as const, label: 'Sign in with Lightspeed', recommended: true, desc: 'Approve Motzu in Lightspeed. We find your account and handle tokens for you.' },
                  { key: 'manual' as const, label: 'Manual credentials', recommended: false, desc: 'For advanced setups. Paste R-Series tokens from an API client your team manages.' },
                ]
              ).map((option) => {
                const selected = lsMethod === option.key;
                return (
                  <Box
                    key={option.key}
                    role="radio"
                    aria-checked={selected}
                    tabIndex={0}
                    onClick={() => setLsMethod(option.key)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setLsMethod(option.key);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      p: 1.75,
                      borderRadius: `${tokens.radius.card}px`,
                      border: '1px solid',
                      borderColor: selected ? tokens.accent.main : tokens.line.default,
                      bgcolor: selected ? tokens.accent.tint : tokens.surface.card,
                      cursor: 'pointer',
                    }}
                  >
                    <Box
                      sx={{
                        mt: 0.25,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        border: '1.5px solid',
                        borderColor: selected ? tokens.accent.main : tokens.ink.disabled,
                        display: 'grid',
                        placeItems: 'center',
                        flex: '0 0 auto',
                      }}
                    >
                      {selected && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tokens.accent.main }} />}
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{option.label}</Typography>
                        {option.recommended && (
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: tokens.signal.positive.ink }}>
                            Recommended
                          </Typography>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, mt: 0.25 }}>{option.desc}</Typography>
                    </Box>
                  </Box>
                );
              })}
              {!lsManual && (
                <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>
                  Sign in with a Lightspeed Retail (R-Series) employee who has full access. Motzu finds your account ID and keeps the tokens refreshed.
                </Typography>
              )}
              {lsManual && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <TextField
                    label="Account ID"
                    value={lsAccountId}
                    error={accountError}
                    helperText={
                      accountError
                        ? 'Use the numeric account ID, like 184203.'
                        : 'Find the account ID in Lightspeed Retail under Settings → Account, or from GET /API/V3/Account.json.'
                    }
                    onChange={(event) => setLsAccountId(event.target.value.trim())}
                    fullWidth
                  />
                  <TextField label="Access token" type="password" required value={lsAccessToken} onChange={(event) => setLsAccessToken(event.target.value)} fullWidth />
                  <TextField label="Refresh token (optional)" type="password" value={lsRefreshToken} onChange={(event) => setLsRefreshToken(event.target.value)} fullWidth />
                  <TextField label="Client ID (optional)" value={lsClientId} onChange={(event) => setLsClientId(event.target.value)} fullWidth />
                  <TextField label="Client secret (optional)" type="password" value={lsClientSecret} onChange={(event) => setLsClientSecret(event.target.value)} fullWidth />
                </Box>
              )}
            </Box>
          )}

          {phase === 'form' && step === 1 && (
            <Box sx={{ px: 3, py: 2.75, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                  {isLs ? 'What Motzu uses' : 'What Motzu can access'}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, mt: 0.5 }}>
                  {isLs
                    ? 'Catalog is always included. The rest are on for most stores — turn one off if another system already owns that data.'
                    : 'Catalog access is always included. The rest are on for most stores — turn one off if another system already owns that data.'}
                </Typography>
                <Typography sx={{ fontSize: 12, color: tokens.ink.muted, mt: 0.75 }}>
                  {isLs
                    ? 'Lightspeed grants access to the whole account in one step, with no per-area permissions. These switches limit what Motzu reads and writes. Which product data it writes back is set after install in Integrations → Sync Behavior.'
                    : 'These control what Motzu can read in Shopify. Which product data Motzu writes back is set after install in Integrations → Sync Behavior.'}
                </Typography>
              </Box>
              <Box sx={{ border: '1px solid', borderColor: tokens.line.default, borderRadius: `${tokens.radius.card}px`, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2, py: 1.5, bgcolor: tokens.surface.sunken }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Catalog</Typography>
                    <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>
                      {isLs ? 'Items, prices, and shops. Needed to sync anything.' : 'Products, listings, and locations. Needed to sync anything.'}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 1, py: 0.25, borderRadius: `${tokens.radius.chip}px`, bgcolor: tokens.surface.tint, color: tokens.ink.bodyAlt, fontSize: 11.5, fontWeight: 600 }}>
                    Always on
                  </Box>
                </Box>
                {(isLs
                  ? [
                      ...(lightspeedSalesEnabled
                        ? [{ key: 'sales' as const, label: 'Sales', desc: 'Read completed sales for sell-through and rebate reports. Never changes a sale or register.' }]
                        : []),
                    ]
                  : SHOPIFY_PERMS
                ).map((row) => {
                  const on = isLs ? lsPerms[row.key as 'sales'] : perms[row.key as ShopifyScopeGroup];
                  const toggle = () => {
                    if (isLs) {
                      const key = row.key as 'sales';
                      setLsPerms((prev) => ({ ...prev, [key]: !prev[key] }));
                    } else {
                      const key = row.key as ShopifyScopeGroup;
                      setPerms((prev) => ({ ...prev, [key]: !prev[key] }));
                    }
                  };
                  return (
                    <Box
                      key={row.key}
                      onClick={toggle}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        px: 2,
                        py: 1.5,
                        borderTop: '1px solid',
                        borderColor: tokens.line.hairline,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: tokens.surface.subtle },
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{row.label}</Typography>
                        <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>{row.desc}</Typography>
                      </Box>
                      <Switch
                        size="small"
                        checked={on}
                        onChange={toggle}
                        onClick={(event) => event.stopPropagation()}
                        inputProps={{ 'aria-label': row.label }}
                      />
                    </Box>
                  );
                })}
              </Box>
              {initialSyncEnabled && !isLs && (
                <Box sx={{ border: '1px solid', borderColor: tokens.line.default, borderRadius: `${tokens.radius.card}px`, px: 2, py: 1.5 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Products Motzu has not seen in your store yet</Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.ink.muted, mt: 0.25, mb: 1.25 }}>
                    Choose whether the first sync creates the full listing (title, description, photos and price) of a selected product that is not in your store. After that, the sync mode you pick next decides what Motzu updates. Choose Pricing and rebates only there if Shopify should keep your own listings, and Motzu will match them by SKU or barcode instead. The starting count below is set on each product Motzu creates. It is separate from the Inventory permission above, and never blocks setup.
                  </Typography>
                  <Box role="radiogroup" aria-label="Products Motzu has not seen in your store yet" sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    {(['create', 'skip'] as const).map((value) => (
                      <Button
                        key={value}
                        role="radio"
                        aria-checked={unmatchedAction === value}
                        size="small"
                        variant={unmatchedAction === value ? 'contained' : 'outlined'}
                        onClick={() => setUnmatchedAction(value)}
                      >
                        {value === 'create' ? 'Create them' : 'Skip them'}
                      </Button>
                    ))}
                  </Box>
                  {unmatchedAction === 'skip' && (
                    <Typography sx={{ fontSize: 12, color: tokens.ink.muted, mb: 1.25 }}>
                      Skip means Motzu does not create new listings. It does not change your sync mode. Your ERP or team creates products.
                    </Typography>
                  )}
                  <NumberField
                    label="Starting inventory count"
                    integer
                    min={0}
                    step={1}
                    adornment="units"
                    value={startingCount === undefined ? '' : String(startingCount)}
                    onChange={(v) => setStartingCount(v === '' ? undefined : Math.max(0, Number(v)))}
                    sx={{ width: 220 }}
                  />
                </Box>
              )}
              <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>{laterNote}</Typography>
            </Box>
          )}

          {phase === 'form' && step === 2 && (
            <Box sx={{ px: 3, py: 2.75, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
              <Box sx={{ border: '1px solid', borderColor: tokens.line.default, borderRadius: `${tokens.radius.card}px`, overflow: 'hidden' }}>
                {[
                  {
                    label: isLs ? 'Account' : 'Store',
                    value: isLs ? (lsManual ? lsAccountId : 'Found when you sign in') : shopDomain,
                    edit: 0,
                  },
                  { label: 'Connection', value: methodLabel, edit: 0 },
                  { label: isLs ? 'Data' : 'Access', value: accessSummary, edit: 1 },
                ].map((row, index) => (
                  <Box
                    key={row.label}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      px: 2,
                      py: 1.5,
                      borderTop: index ? '1px solid' : 'none',
                      borderColor: tokens.line.hairline,
                    }}
                  >
                    <Typography sx={{ width: 110, flex: '0 0 110px', fontSize: 12.5, color: tokens.ink.muted }}>{row.label}</Typography>
                    <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{row.value}</Typography>
                    <Button size="small" onClick={() => setStep(row.edit)} sx={{ minWidth: 0, fontWeight: 600 }}>
                      Edit
                    </Button>
                  </Box>
                ))}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>Your data</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                  <Box sx={{ p: 2, border: '1px solid', borderColor: tokens.line.default, borderRadius: `${tokens.radius.card}px` }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tokens.signal.positive.ink, mb: 1 }}>
                      Motzu copies
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: tokens.ink.body, lineHeight: 1.55 }}>{copiesText}</Typography>
                  </Box>
                  <Box sx={{ p: 2, border: '1px solid', borderColor: tokens.line.default, borderRadius: `${tokens.radius.card}px` }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tokens.signal.problem.ink, mb: 1 }}>
                      Never copied
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: tokens.ink.body, lineHeight: 1.55 }}>
                      Customer names, emails, phone numbers, or addresses.
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ mt: 1.5, fontSize: 12, color: tokens.ink.muted }}>
                  Kept while the integration is connected. Deleted when you disconnect, close your Motzu account, or ask us to.{' '}
                  <Box component="a" href="/privacy" sx={{ fontWeight: 600, color: tokens.accent.main }}>
                    Privacy Policy
                  </Box>
                </Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: tokens.surface.sunken, border: '1px solid', borderColor: tokens.line.default, borderRadius: `${tokens.radius.card}px` }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>Product sync after install</Typography>
                <Typography sx={{ fontSize: 12.5, color: tokens.ink.body, lineHeight: 1.55 }}>
                  {isLs
                    ? lightspeedLinkOnlyEnabled
                      ? 'After you connect, open Integrations → Sync Behavior to choose the sync mode. Full sync creates and updates Lightspeed items from your Motzu catalog. Link only matches existing items by UPC, manufacturer SKU, or model and never creates or changes them. Run a catalog scan first to see what matches.'
                      : 'After you connect, open Integrations → Sync Behavior to choose the sync mode. Full sync creates and updates Lightspeed items from your Motzu catalog.'
                    : 'After you connect, open Integrations → Sync Behavior to choose which product data Motzu writes (products, pricing, images) and the sync mode (link only, enrich, or override). Those are not extra Shopify permissions.'}
                </Typography>
              </Box>
            </Box>
          )}

          {phase === 'verifying' && (
            <Box sx={{ px: 4, py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, textAlign: 'center' }}>
              <CircularProgress size={28} />
              <Typography sx={{ fontSize: 16, fontWeight: 700, mt: 1 }}>Checking your credentials…</Typography>
              <Typography sx={{ fontSize: 13, color: tokens.ink.muted }}>
                Confirming Motzu can reach {targetName}.
              </Typography>
            </Box>
          )}

          {phase === 'done' && (
            <Box sx={{ px: 4, py: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: tokens.signal.positive.tint,
                  color: tokens.signal.positive.ink,
                }}
              >
                <Check />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, mt: 0.5 }}>{targetName} is connected</Typography>
              <Typography sx={{ fontSize: 13, color: tokens.ink.muted, maxWidth: 400 }}>
                {isLs
                  ? 'Next, scan your Lightspeed catalog to see which items already match Motzu products. The scan is read-only — nothing changes until you run the first sync.'
                  : shopifyScanFirst
                    ? 'Next, scan your Shopify catalog to see which listings already match Motzu products, then choose which products to sync. The scan is read-only — nothing changes until you run the first sync.'
                    : 'Next, scan your Shopify catalog to see which listings already match the products you selected. The scan is read-only — nothing changes until you run the first sync.'}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 3,
              py: 2,
              borderTop: '1px solid',
              borderColor: tokens.line.hairline,
            }}
          >
            {phase === 'form' && step > 0 && (
              <Button variant="outlined" onClick={() => setStep(step - 1)} disabled={busy} sx={{ height: 38, borderRadius: `${tokens.radius.control}px` }}>
                Back
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            {phase !== 'verifying' && (
              <Button onClick={onClose} disabled={busy} sx={{ height: 38, color: tokens.ink.bodyAlt, fontWeight: 600 }}>
                {phase === 'done' ? 'Close' : 'Cancel'}
              </Button>
            )}
            {phase !== 'verifying' && (
              <Button
                variant="contained"
                onClick={onPrimary}
                disabled={phase === 'form' && (!canNext || busy)}
                endIcon={primaryExternal ? <OpenInNew sx={{ fontSize: 16 }} /> : undefined}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
                sx={{
                  height: 38,
                  borderRadius: `${tokens.radius.control}px`,
                  '&.Mui-disabled': {
                    bgcolor: tokens.line.default,
                    color: tokens.ink.faint,
                  },
                }}
              >
                {primaryLabel}
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default IntegrationInstallWizard;
